import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast, SkeletonLoader } from "@/components/system";
import {
  Row,
  FormInput,
  FormSelect,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  localNowDatetime,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * Quick-log workout (SPEC 3.1.4). Writes one row to `session` with
 * sensor_source='manual'. Required: sport_id, duration_min, start_ts.
 *
 * Sport options pulled from the user's `sport` table (24 seeded by
 * bootstrap_user_seeds + any auto-created from Garmin imports later).
 *
 * end_ts derived from start_ts + duration_min so the spec's required
 * end_ts column is satisfied even when the user didn't watch a clock.
 */

export function LogWorkoutDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const sportsQuery = useQuery({
    queryKey: ["sports", userId],
    enabled: Boolean(userId) && open,
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sport")
        .select("id, name, routing_rule")
        .eq("user_id", userId!)
        .order("name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [sportId, setSportId] = useState("");
  const [duration, setDuration] = useState("");
  const [distanceKm, setDistanceKm] = useState("");
  const [rpe, setRpe] = useState("");
  const [notes, setNotes] = useState("");
  const [startTs, setStartTs] = useState(localNowDatetime());

  const mutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      if (!sportId) throw new Error("Pick a sport");
      if (!duration) throw new Error("Duration is required");
      const start = new Date(startTs);
      const end = new Date(start.getTime() + Number(duration) * 60_000);
      const { data, error } = await supabase
        .from("session")
        .insert({
          user_id: userId,
          start_ts: start.toISOString(),
          end_ts: end.toISOString(),
          tz: browserTz(),
          sport_id: sportId,
          duration_min: Number(duration),
          distance_km: distanceKm ? Number(distanceKm) : null,
          rpe: rpe ? Number(rpe) : null,
          notes,
          sensor_source: "manual",
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sessions", userId] });
      toast.show("Workout logged.");
      reset();
      onClose();
    },
    onError: (e) => toast.show(`Couldn't log workout: ${formatError(e)}`),
  });

  function reset() {
    setSportId("");
    setDuration("");
    setDistanceKm("");
    setRpe("");
    setNotes("");
    setStartTs(localNowDatetime());
  }

  const sports = sportsQuery.data ?? [];

  return (
    <Drawer
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Log workout"
    >
      {sportsQuery.isLoading ? (
        <SkeletonLoader variant="block" count={3} />
      ) : sports.length === 0 ? (
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No sports yet. Run the seed loader from Settings → Reload seeds.
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <FormSelect
            label="Sport"
            value={sportId}
            onChange={setSportId}
            options={[
              { value: "", label: "Pick one" },
              ...sports
                .filter((s) => s.routing_rule === "session")
                .map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
          <Row>
            <FormInput
              label="Duration (min)"
              inputMode="decimal"
              value={duration}
              onChange={setDuration}
              placeholder="60"
              required
            />
            <FormInput
              label="Distance (km)"
              inputMode="decimal"
              value={distanceKm}
              onChange={setDistanceKm}
              placeholder="—"
            />
            <FormInput
              label="RPE 1–10"
              inputMode="numeric"
              value={rpe}
              onChange={setRpe}
              placeholder="6"
              min={1}
              max={10}
            />
          </Row>
          <FormInput
            label="Started"
            type="datetime-local"
            value={startTs}
            onChange={setStartTs}
          />
          <FormTextarea label="Notes (optional)" value={notes} onChange={setNotes} />

          <DialogFooter>
            <SecondaryButton
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={mutation.isPending || !sportId || !duration}
            >
              {mutation.isPending ? "Saving…" : "Log workout"}
            </PrimaryButton>
          </DialogFooter>
        </form>
      )}
    </Drawer>
  );
}
